import { useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { apiServicesService, type ApiInstanceDeleteResponse, type ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ProjectGroup } from '@/deploy/types';
import { ENVIRONMENT_BY_ID } from '@/deploy/types';
import { API_ENV_GROUPS, apiGroupLabel } from '@/deploy/utils/apiEnvs';
import {
  citationsText, findInstance, instanceCitations, instanceDisplayName, instanceErrorMessage, instanceLabel, linkOn,
} from '@/deploy/utils/apiInstances';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';
import styles from './ApiInstances.module.scss';

// ============================================================
// Suppression d'une instance secondaire. Refusée tant qu'une répartition la cite (le 409 du
// CICD reste la garde en cas de course) ; ses serveurs Ptero restent, ou sont supprimés.
// ============================================================

export function ApiInstanceDeleteModal({ projectId, api, instanceKey, groups, onClose, onChanged, onOpenRouting }: {
  projectId: string;
  api: ApiService;
  instanceKey: string;
  groups: ProjectGroup[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  /** Ouvre la répartition de l'API (bouton de l'avertissement « citée »). */
  onOpenRouting?: () => void;
}) {
  const inst = findInstance(api, instanceKey);
  const [deleteServers, setDeleteServers] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApiInstanceDeleteResponse | null>(null);
  if (!inst && !result) return null;

  const label = inst ? instanceLabel(inst) : instanceKey;
  const name = inst ? instanceDisplayName(api, inst) : `${api.name} [${instanceKey}]`;
  const cited = inst ? instanceCitations(api, inst.key) : [];
  // Serveurs rattachés (id stocké) : bound, et aussi missing ou broken qu'il faut délier
  const servers: { env: ApiEnvId; name: string }[] = inst
    ? API_ENV_GROUPS.filter((e) => ['bound', 'missing', 'broken'].includes(linkOn(inst, e) ?? ''))
      .map((e) => ({ env: e, name: inst.serverNames?.[e] ?? name }))
    : [];
  const sensitive = deleteServers && servers.some((s) => ENVIRONMENT_BY_ID[s.env].requiresConfirmation);
  const confirmed = !sensitive || confirmText.trim() === label;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiServicesService.deleteInstance(projectId, api.id, instanceKey, deleteServers);
      setResult(res);
      await onChanged();
      if (res.results.every((r) => r.ok)) onClose();
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Supprimer l'instance ${label} de ${api.name}`}
      subtitle="La Principale et les autres instances ne sont pas touchées"
      width="md"
      footer={result ? (
        <Button variant="primary" onClick={onClose}>Fermer</Button>
      ) : (<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="danger" onClick={() => void submit()} loading={submitting}
          disabled={submitting || cited.length > 0 || !confirmed} leftIcon={<Icon name="x" size={12} />}>
          Supprimer l'instance
        </Button>
      </>)}
    >
      <div className={pageStyles.form}>
        {error && <div className={pageStyles.error}>{error}</div>}
        {cited.length > 0 && !result && (
          <div className={[pageStyles.resultRow, pageStyles.resultWarn].join(' ')}>
            <Icon name="warn" size={12} />
            <span style={{ flex: 1 }}>
              Elle sert encore {citationsText(cited, groups)} : change la répartition d'abord.
            </span>
            {onOpenRouting && (
              <Button variant="secondary" size="sm" onClick={onOpenRouting}>Ouvrir la répartition</Button>
            )}
          </div>
        )}

        {!result && (
          <div className={styles.routeList}>
            <label className={pageStyles.envCheck} style={{ justifyContent: 'flex-start' }}>
              <input type="radio" name="deleteServers" checked={!deleteServers} onChange={() => setDeleteServers(false)} />
              <span>Retirer du suivi seulement : les serveurs Ptero restent, sans lien avec cette API</span>
            </label>
            <label className={pageStyles.envCheck} style={{ justifyContent: 'flex-start' }}>
              <input type="radio" name="deleteServers" checked={deleteServers} onChange={() => setDeleteServers(true)}
                disabled={servers.length === 0} />
              <span>
                {servers.length > 0
                  ? <>Supprimer aussi ses serveurs Ptero : {servers.map((s) => `${apiGroupLabel(s.env)} (${s.name})`).join(', ')}</>
                  : 'Supprimer aussi ses serveurs Ptero : aucun serveur rattaché'}
              </span>
            </label>
          </div>
        )}

        {sensitive && !result && (
          <label className={pageStyles.field}>
            <span>Pour confirmer, saisis le libellé <em>{label}</em></span>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={label} />
          </label>
        )}

        {result && (
          <div className={pageStyles.results}>
            {result.results.length === 0 && (
              <div className={[pageStyles.resultRow, pageStyles.resultOk].join(' ')}>
                <Icon name="check" size={12} />
                <span style={{ flex: 1 }}>Instance supprimée</span>
              </div>
            )}
            {result.results.map((r) => (
              <div key={r.environment}
                className={[pageStyles.resultRow, r.ok ? pageStyles.resultOk : pageStyles.resultKo].join(' ')}>
                <Icon name={r.ok ? 'check' : 'x'} size={12} />
                <strong>{apiGroupLabel(r.environment)}</strong>
                <span style={{ flex: 1 }}>
                  {r.ok
                    ? <>{r.serverName ?? name} supprimé</>
                    : <>suppression KO, l'instance est gardée{r.error ? ` : ${r.error}` : ''}</>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
